import {
  Alert,
  Button,
  Group,
  Modal,
  MultiSelect,
  SegmentedControl,
  Stack,
  Text,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { ManualAbsence } from "@msc/shared";

import {
  useCreateManualAbsences,
  useUpdateManualAbsence,
} from "../../hooks/useAbsences.js";
import { GERMAN_DATE_FORMAT, fromLocalDateString } from "../../lib/date.js";
import {
  type AbsenceFormStand,
  LEERES_ABSENCE_FORMULAR,
  anlegeEingabe,
  endeNachziehen,
  zeitraumEingabe,
} from "./absenceForm.js";

/**
 * Der Erfassungsdialog — einmal gebaut, an zwei Stellen benutzt.
 *
 * Er sitzt in der Pflegeansicht unter Einstellungen **und** in der
 * Berichtsbearbeitung. Zwei Dialoge mit demselben Formular wären zwei Orte, an
 * denen die Datumsumrechnung schiefgehen kann — und genau die ist hier die
 * einzige lautlose Fehlerquelle (s. `absenceForm.ts`).
 *
 * Die beiden Aufrufer unterscheiden sich nur darin, WER erfasst wird: In den
 * Einstellungen wählt man die Personen aus einer Liste, im Bericht steht die
 * Person schon fest — es ist der Ansprechpartner, dessen Zeile man gerade
 * ansieht.
 */

export interface AbsencePersonOption {
  id: string;
  fullName: string;
}

export interface AbsenceModalProps {
  opened: boolean;
  onClose: () => void;
  /** Gesetzt = Bearbeiten eines bestehenden Eintrags. */
  bearbeitet?: ManualAbsence | null;
  /** Auswahl mehrerer Personen (Pflegeansicht). */
  auswahl?: AbsencePersonOption[];
  /** Feste Person (Berichtsbearbeitung) — dann ohne Auswahlfeld. */
  person?: AbsencePersonOption;
}

function startStand(props: AbsenceModalProps): AbsenceFormStand {
  if (props.bearbeitet) {
    return {
      employeeIds: [props.bearbeitet.employeeId],
      dateFrom: fromLocalDateString(props.bearbeitet.dateFrom),
      dateTo: fromLocalDateString(props.bearbeitet.dateTo),
      dayFraction: props.bearbeitet.dayFraction === 0.5 ? "0.5" : "1",
    };
  }
  return {
    ...LEERES_ABSENCE_FORMULAR,
    employeeIds: props.person ? [props.person.id] : [],
  };
}

export function AbsenceModal(props: AbsenceModalProps) {
  const { t } = useTranslation();
  const anlegen = useCreateManualAbsences();
  const aendern = useUpdateManualAbsence();
  // Der Zustand wird beim Einhängen gesetzt; die Aufrufer geben dem Dialog
  // deshalb ein `key`, damit ein anderer Eintrag ihn neu aufbaut (Befund W5).
  const [form, setForm] = useState<AbsenceFormStand>(() => startStand(props));
  const [fehler, setFehler] = useState<string | null>(null);

  const bearbeitet = props.bearbeitet ?? null;
  const mitAuswahl = !bearbeitet && props.auswahl !== undefined;

  async function speichern() {
    setFehler(null);
    try {
      if (bearbeitet) {
        const input = zeitraumEingabe(form);
        if (!input) return;
        await aendern.mutateAsync({ id: bearbeitet.id, input });
      } else {
        const input = anlegeEingabe(form);
        if (!input) return;
        await anlegen.mutateAsync(input);
      }
      props.onClose();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    }
  }

  const speichernMoeglich =
    form.dateFrom !== null &&
    form.dateTo !== null &&
    form.employeeIds.length > 0;

  const titel = bearbeitet
    ? t("pages.settings.absences.editTitle", {
        name: bearbeitet.employeeName,
      })
    : props.person
      ? t("pages.settings.absences.addTitleFor", { name: props.person.fullName })
      : t("pages.settings.absences.addTitle");

  return (
    <Modal opened={props.opened} onClose={props.onClose} title={titel}>
      <Stack gap="sm">
        {mitAuswahl ? (
          <>
            <MultiSelect
              label={t("pages.settings.absences.people")}
              description={t("pages.settings.absences.peopleHint")}
              searchable
              data={(props.auswahl ?? []).map((e) => ({
                value: e.id,
                label: e.fullName,
              }))}
              value={form.employeeIds}
              onChange={(employeeIds) => setForm((f) => ({ ...f, employeeIds }))}
            />
            <Group>
              <Button
                size="xs"
                variant="subtle"
                disabled={(props.auswahl ?? []).length === 0}
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    employeeIds: (props.auswahl ?? []).map((e) => e.id),
                  }))
                }
              >
                {t("pages.settings.absences.selectAll")}
              </Button>
            </Group>
          </>
        ) : null}

        <Group grow>
          <DatePickerInput
            label={t("pages.settings.absences.from")}
            valueFormat={GERMAN_DATE_FORMAT}
            value={form.dateFrom}
            onChange={(dateFrom) => setForm((f) => endeNachziehen(f, dateFrom))}
          />
          <DatePickerInput
            label={t("pages.settings.absences.to")}
            valueFormat={GERMAN_DATE_FORMAT}
            minDate={form.dateFrom ?? undefined}
            value={form.dateTo}
            onChange={(dateTo) => setForm((f) => ({ ...f, dateTo }))}
          />
        </Group>

        <div>
          <Text size="sm" fw={500} mb={4}>
            {t("pages.settings.absences.fraction")}
          </Text>
          <SegmentedControl
            fullWidth
            data={[
              { value: "1", label: t("pages.settings.absences.fullDay") },
              { value: "0.5", label: t("pages.settings.absences.halfDay") },
            ]}
            value={form.dayFraction}
            onChange={(dayFraction) =>
              setForm((f) => ({
                ...f,
                dayFraction: dayFraction === "0.5" ? "0.5" : "1",
              }))
            }
          />
        </div>

        <Text size="xs" c="dimmed">
          {t("pages.settings.absences.noReasonHint")}
        </Text>

        {fehler ? (
          <Alert color="red" variant="light">
            {fehler}
          </Alert>
        ) : null}

        <Group justify="flex-end">
          <Button variant="default" onClick={props.onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={!speichernMoeglich}
            loading={anlegen.isPending || aendern.isPending}
            onClick={() => void speichern()}
          >
            {t("common.save")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
